import { ArrowRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal } from "@/components/Reveal";
import { SHOWCASES, IMAGES } from "@/data/content";
import { scrollToHash } from "@/components/Navbar";
import { cn } from "@/lib/utils";

const ShowcaseBlock = ({ data, dark, reverse, testid }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

    return (
        <section ref={ref} data-testid={testid}
            className={cn("relative overflow-hidden py-24 lg:py-32", dark ? "blueprint noise" : "bg-omsivo-paper")}>
            <div className="relative z-10 mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-10">
                <div className={cn(reverse && "lg:order-2")}>
                    <Reveal>
                        <p className={cn("font-mono text-[11px] font-semibold uppercase tracking-[0.28em]", dark ? "text-flame" : "text-flame-dark")}>
                            {data.kicker}
                        </p>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <h2 className={cn("mt-4 font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl", dark ? "text-white" : "text-omsivo-navy")}>
                            {data.title[0]}<br />{data.title[1]}
                        </h2>
                    </Reveal>
                    {data.headline2 && (
                        <Reveal delay={0.14}>
                            <p className={cn("mt-4 font-display text-5xl font-black uppercase tracking-tight sm:text-6xl", dark ? "text-outline-light" : "text-outline-dark")}>
                                {data.headline2}
                            </p>
                        </Reveal>
                    )}
                    <Reveal delay={0.16}>
                        <div className="mt-8 flex flex-wrap gap-2">
                            {data.items.map((it) => (
                                <span key={it} className={cn(
                                    "rounded-full border px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em]",
                                    dark ? "border-white/12 bg-white/5 text-white/70" : "border-omsivo-navy/12 bg-white text-omsivo-slate shadow-sm"
                                )}>
                                    {it}
                                </span>
                            ))}
                        </div>
                    </Reveal>
                    <Reveal delay={0.22}>
                        <button
                            onClick={() => scrollToHash("#contact")}
                            data-testid={`${testid}-cta`}
                            className="group mt-9 inline-flex items-center gap-3 rounded-full bg-flame px-7 py-3.5 font-display text-sm font-bold uppercase tracking-[0.1em] text-white transition-all hover:-translate-y-0.5 hover:bg-flame-dark"
                        >
                            {data.cta}
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </button>
                    </Reveal>
                </div>
                <Reveal className={cn(reverse && "lg:order-1")} delay={0.1}>
                    <div className="spotlight-frame overflow-hidden rounded-[28px] border border-omsivo-navy/10 shadow-2xl shadow-omsivo-navy/20"
                        style={{ clipPath: reverse ? "polygon(0 0, 100% 0, 100% 100%, 12% 100%, 0 88%)" : "polygon(0 0, 100% 0, 100% 88%, 88% 100%, 0 100%)" }}>
                        <motion.img
                            src={IMAGES[data.image]}
                            alt={data.kicker}
                            loading="lazy"
                            style={{ y, scale: 1.12 }}
                            className="h-[320px] w-full object-cover sm:h-[420px] lg:h-[480px]"
                            data-testid={`${testid}-image`}
                        />
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

const MATERIAL_JOURNEY = ["DISCOVER", "SELECT", "COMPARE", "BUY", "DELIVER"];

export const MaterialsSection = () => (
    <section id="materials" data-testid="materials-section" className="blueprint noise relative overflow-hidden py-24 lg:py-32">
        <div className="relative z-10 mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
            <div className="mx-auto max-w-3xl text-center">
                <Reveal>
                    <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-flame">Building Materials</p>
                </Reveal>
                <Reveal delay={0.08}>
                    <h2 className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
                        Building materials.<br />Coming to OMSIVO.
                    </h2>
                </Reveal>
                <Reveal delay={0.14}>
                    <p className="mt-5 text-base leading-relaxed text-white/65">
                        A construction material marketplace is being built into the OMSIVO ecosystem — sourcing, comparing and ordering materials for your project, end to end.
                    </p>
                </Reveal>
            </div>
            <Reveal delay={0.2}>
                <div className="mx-auto mt-12 flex max-w-4xl flex-wrap items-center justify-center gap-3" data-testid="materials-journey">
                    {MATERIAL_JOURNEY.map((s, i) => (
                        <div key={s} className="flex items-center gap-3">
                            <span className="rounded-full border border-white/15 bg-white/5 px-5 py-2.5 font-display text-xs font-bold uppercase tracking-[0.16em] text-white">
                                {s}
                            </span>
                            {i < MATERIAL_JOURNEY.length - 1 && <ArrowRight className="h-4 w-4 text-flame" />}
                        </div>
                    ))}
                </div>
            </Reveal>
            <Reveal delay={0.26}>
                <div className="mt-10 text-center">
                    <span className="inline-block rounded-full bg-flame px-5 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.28em] text-white" data-testid="materials-coming-soon">
                        Coming Soon
                    </span>
                </div>
            </Reveal>
        </div>
    </section>
);

export const Showcases = () => (
    <>
        <ShowcaseBlock data={SHOWCASES.engineering} testid="engineering-section" />
        <ShowcaseBlock data={SHOWCASES.industrial} dark reverse testid="industrial-section" />
        <ShowcaseBlock data={SHOWCASES.mega} testid="mega-section" />
        <ShowcaseBlock data={SHOWCASES.interior} dark reverse testid="interior-section" />
    </>
);
